import React, { createContext, useContext } from 'react';

/**
 * HOME-01 — the one thing the two render trees genuinely disagree about.
 *
 * The redesign's modules are shared by the Vike SSR page (src/components/**)
 * and the React Router SPA (pages/Home.tsx, which also IS the native build).
 * The SSR tree has no Router above it — a `<Link>` there throws — and the SPA
 * must not use bare `<a href>`, because in the Capacitor WebView a full
 * document navigation tears down and restarts the app shell.
 *
 * So the modules never decide. They render `<HomeLink to="...">` and the page
 * shell supplies the renderer: the SSR shell supplies none and gets `<a>`, the
 * SPA shell supplies a react-router `<Link>` wrapper. Nothing in this directory
 * imports react-router-dom, which is what keeps the SSR bundle able to load it.
 */

export interface HomeLinkRenderProps {
  to: string;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  'aria-label'?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  /**
   * "This destination is not an SPA route — leave the app to reach it."
   *
   * /guides and the rest of the Vike-only pages have no entry in App.tsx's
   * router (REL-03), so a client-side push at one of them lands on the SPA's
   * unmatched-path handler instead of the page. The SPA renderer therefore has
   * to emit a real anchor for those, and the only place that knows which is the
   * call site. Ignored by the anchor renderer, which is already leaving.
   */
  external?: boolean;
}

export type HomeLinkRenderer = React.ComponentType<HomeLinkRenderProps>;

/**
 * The default: a plain anchor. Correct for the server render and for the first
 * client paint before hydration swaps in the SPA, and correct on any surface
 * that genuinely has no router.
 */
const AnchorLink: HomeLinkRenderer = ({ to, children, external: _external, ...rest }) => (
  <a href={to} {...rest}>
    {children}
  </a>
);

const HomeLinkContext = createContext<HomeLinkRenderer>(AnchorLink);

export const HomeLinkProvider: React.FC<{
  renderer: HomeLinkRenderer;
  children: React.ReactNode;
}> = ({ renderer, children }) => (
  <HomeLinkContext.Provider value={renderer}>{children}</HomeLinkContext.Provider>
);

const HomeLink: React.FC<HomeLinkRenderProps> = (props) => {
  const Renderer = useContext(HomeLinkContext);
  return <Renderer {...props} />;
};

export default HomeLink;
